import { useState } from 'react';
import { GripVertical, Zap, Search, Filter, Tag, CheckSquare, Square, X } from 'lucide-react';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import { CONVERSATION_STATUS, CONVERSATION_STATUS_LABELS } from '../../data/conversations';

const CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'web', label: 'Web' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'email', label: 'E-mail' },
  { id: 'outros', label: 'Outros' },
];

export default function ConversationFunnel({
  conversations,
  funnelStages,
  teams = {},
  contacts = [],
  onSelectConversation,
  onStageChange,
  searchQuery = '',
  onSearchQueryChange,
  teamFilterId = null,
  onTeamFilterIdChange,
  channelFilter = null,
  onChannelFilterChange,
  tagFilterId = null,
  onTagFilterIdChange,
  tags = [],
  statusFilter = [],
  onStatusFilterChange,
}) {
  const stages = funnelStages?.length ? funnelStages : [];
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const teamsList = Array.isArray(teams) ? teams : Object.values(teams);
  const statusArr = Array.isArray(statusFilter) ? statusFilter : [];
  const hasActiveFilters = statusArr.length > 0 || teamFilterId || channelFilter || searchQuery.trim();

  const getContactForConversation = (c) => {
    if (!c || !contacts.length) return null;
    const byId = c.contact_id && contacts.find((ct) => ct.id === c.contact_id);
    if (byId) return byId;
    const phoneNorm = (v) => (v || '').replace(/\D/g, '');
    const convPhone = phoneNorm(c.phone);
    const convEmail = (c.email || '').trim().toLowerCase();
    return contacts.find((ct) => (convPhone && phoneNorm(ct.telefone) === convPhone) || (convEmail && (ct.email || '').trim().toLowerCase() === convEmail)) || null;
  };

  const getConversationsByStage = (stageId, stageSlug) =>
    conversations.filter((c) => {
      const value = c.stage || 'triagem';
      return value === stageId || (stageSlug && value === stageSlug);
    });

  const handleDragStart = (e, convId) => {
    setDraggedId(convId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(convId));
    e.dataTransfer.setData('application/json', JSON.stringify({ convId }));
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = (e, stageKey) => {
    e.preventDefault();
    setDragOverStage(null);
    const convId = e.dataTransfer.getData('text/plain');
    if (convId && onStageChange) {
      onStageChange(convId, stageKey);
    }
    setDraggedId(null);
  };

  const handleCardClick = (e, convId) => {
    if (draggedId) return;
    onSelectConversation?.(convId);
  };

  return (
    <div className="flex-1 flex flex-col bg-appPage overflow-hidden">
      <div className="px-8 pt-6 pb-4 shrink-0 border-b border-appSurfaceBorder bg-appSurface">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-appText tracking-tight">Funil de Conversas</h1>
            <p className="text-sm text-appTextMuted mt-1">
              Arraste os cards entre as colunas para mover as conversas de etapa
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div
              className={`flex items-center gap-2 rounded-lg transition-all overflow-hidden ${
                searchFocused ? 'bg-appSurfaceBorder/30' : ''
              }`}
            >
              <label className="flex items-center gap-2 cursor-text py-2 px-2.5 rounded-lg border border-appSurfaceBorder bg-white/50">
                <Search size={18} className="text-appTextMuted shrink-0" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => onSearchQueryChange?.(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Buscar..."
                  className="bg-transparent border-none outline-none text-sm text-appText placeholder:text-appTextMuted w-32 lg:w-40 py-0.5"
                />
              </label>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => onSearchQueryChange?.('')}
                  className="p-1 text-appTextMuted hover:text-appText rounded"
                  aria-label="Limpar busca"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {tags.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setTagFilterOpen((v) => !v); setFilterMenuOpen(false); }}
                  className={`p-2 rounded-lg border border-appSurfaceBorder transition-colors ${tagFilterOpen || tagFilterId ? 'bg-appPrimary/20 text-appPrimary border-appPrimary/40' : 'text-appTextMuted hover:bg-appSurfaceBorder/30 bg-white/50'}`}
                  aria-label="Filtrar por etiqueta"
                >
                  <Tag size={18} />
                </button>
                {tagFilterOpen && (
                  <>
                    <div className="fixed inset-0 z-10" aria-hidden onClick={() => setTagFilterOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-appSurface border border-appSurfaceBorder rounded-xl shadow-lg py-3 px-3 max-h-[70vh] overflow-y-auto">
                      <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-1 mb-2">Etiquetas</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => { onTagFilterIdChange?.(null); setTagFilterOpen(false); }}
                          className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${!tagFilterId ? 'bg-appPrimary/20 text-appPrimary ring-1 ring-appPrimary/40' : 'bg-appSurfaceBorder/30 text-appTextMuted hover:bg-appSurfaceBorder/50'}`}
                        >
                          Todas
                        </button>
                        {tags.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => { onTagFilterIdChange?.(tagFilterId === t.id ? null : t.id); setTagFilterOpen(false); }}
                            className="focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-appPrimary rounded-full"
                          >
                            <Badge
                              colorClass={`${t.color || 'bg-appSurfaceBorder text-appText'} border ${t.border || 'border-transparent'} ${tagFilterId === t.id ? 'ring-2 ring-appPrimary ring-offset-1' : ''}`}
                            >
                              {t.name}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => { setFilterMenuOpen((v) => !v); setTagFilterOpen(false); }}
                className={`p-2 rounded-lg border border-appSurfaceBorder transition-colors ${filterMenuOpen || hasActiveFilters ? 'bg-appPrimary/20 text-appPrimary border-appPrimary/40' : 'text-appTextMuted hover:bg-appSurfaceBorder/30 bg-white/50'}`}
                aria-label="Filtros"
              >
                <Filter size={18} />
              </button>
              {filterMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden onClick={() => setFilterMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-appSurface border border-appSurfaceBorder rounded-xl shadow-lg py-3 px-2 max-h-[70vh] overflow-y-auto">
                    <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-2">Filtros</p>
                    <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-1.5">Status da conversa</p>
                    {[CONVERSATION_STATUS.ATIVO, CONVERSATION_STATUS.AGUARDANDO, CONVERSATION_STATUS.FINALIZADO].map((value) => {
                      const isSelected = statusArr.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            const next = isSelected ? statusArr.filter((s) => s !== value) : [...statusArr, value];
                            onStatusFilterChange?.(next);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${isSelected ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                        >
                          {isSelected ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                          {CONVERSATION_STATUS_LABELS[value]}
                        </button>
                      );
                    })}
                    <div className="border-t border-appSurfaceBorder my-2" />
                    {teamsList.length > 0 && (
                      <>
                        <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-1.5">Equipe</p>
                        <button
                          type="button"
                          onClick={() => { onTeamFilterIdChange?.(null); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${!teamFilterId ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                        >
                          {!teamFilterId ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                          Todas
                        </button>
                        {teamsList.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => { onTeamFilterIdChange?.(t.id); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${teamFilterId === t.id ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                          >
                            {teamFilterId === t.id ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                            {t.name}
                          </button>
                        ))}
                        <div className="border-t border-appSurfaceBorder my-2" />
                      </>
                    )}
                    <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-1.5">Canal de venda</p>
                    <button
                      type="button"
                      onClick={() => { onChannelFilterChange?.(null); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${!channelFilter ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                    >
                      {!channelFilter ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                      Todos
                    </button>
                    {CHANNELS.map((ch) => (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => { onChannelFilterChange?.(ch.id); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${channelFilter === ch.id ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                      >
                        {channelFilter === ch.id ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                        {ch.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto overflow-y-auto p-6">
        <div className="flex gap-5 h-full min-w-max">
          {stages.map((stage) => {
            const stageKey = stage.slug || stage.id;
            const stageConversations = getConversationsByStage(stage.id, stage.slug);
            const isDropTarget = dragOverStage === stage.id;

            return (
              <div
                key={stage.id}
                className={`flex flex-col w-80 shrink-0 rounded-2xl border-2 transition-all duration-200 bg-appSurface shadow-sm ${
                  stage.color
                } ${isDropTarget ? 'ring-2 ring-appPrimary ring-offset-2 scale-[1.02]' : 'border-appSurfaceBorder'}`}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stageKey)}
              >
                <div
                  className={`px-4 py-3 rounded-t-2xl font-bold text-sm ${stage.headerColor} flex items-center justify-between shrink-0`}
                >
                  <span>{stage.label}</span>
                  <span className="bg-white/70 px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm">
                    {stageConversations.length}
                  </span>
                </div>

                <div className={`flex-1 p-3 overflow-y-auto space-y-2 min-h-[280px] rounded-b-2xl transition-colors ${
                  isDropTarget && stageConversations.length === 0 ? 'bg-appPrimary/5 border-2 border-dashed border-appPrimary/40' : ''
                }`}>
                  {stageConversations.length === 0 && isDropTarget && (
                    <p className="text-center text-sm text-appTextMuted py-8">Solte aqui</p>
                  )}
                  {stageConversations.map((conv) => (
                    <div
                      key={conv.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, conv.id)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => handleCardClick(e, conv.id)}
                      className={`group bg-white rounded-xl p-3 border border-appSurfaceBorder shadow-sm cursor-grab active:cursor-grabbing hover:shadow-lg hover:border-appPrimary/30 transition-all select-none ${
                        draggedId === conv.id ? 'opacity-40 scale-95 rotate-1' : 'hover:scale-[1.02]'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 text-appTextMuted cursor-grab active:cursor-grabbing">
                          <GripVertical size={14} />
                        </div>
                        <Avatar src={conv.avatarUrl} label={conv.avatarLabel ?? conv.avatar} size="sm" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-appText truncate">
                            {conv.name}
                          </p>
                          <p className="text-xs text-appTextMuted truncate mt-0.5">
                            {conv.lastMessage}
                          </p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {conv.aiActive && (
                              <Badge
                                colorClass="bg-appPrimaryLight text-appPrimaryText border-appPrimary/30 flex items-center gap-1"
                              >
                                <Zap size={8} /> IA
                              </Badge>
                            )}
                            {(getContactForConversation(conv)?.tags || [])
                              .map((tagId) => tags.find((t) => t.id === tagId))
                              .filter(Boolean)
                              .map((t) => (
                                <Badge key={t.id} colorClass={`${t.color || 'bg-appSurfaceBorder text-appText'} border ${t.border || 'border-transparent'}`}>{t.name}</Badge>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
